import React, { useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import ListItem from '@tiptap/extension-list-item';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import { NodeSelection } from '@tiptap/pm/state';
import { Lock } from 'lucide-react';
import api from '../../../../services/api';
import { uploadImage, deleteImage } from '../../services/rfpCoreService';
import SuccessToast from '../../../../shared/components/ui/SuccessToast';

// ── Collaboration (additive – safe to tree-shake when props not provided) ─────
import CollaborationBar, { useTypingIndicator } from './CollaborationBar';
import Collaboration, { isChangeOrigin } from '@tiptap/extension-collaboration';
import CollaborationCaret from '@tiptap/extension-collaboration-caret';

// ── Extracted extensions ───────────────────────────────────────────────────────
import CustomImage from './extensions/ImageExtension';
import { CustomOrderedList, CustomBulletList, CustomTableCell, CustomTableHeader, RaciDecorationExtension, applyRaciColors } from './extensions/EditorExtensions';
import sanitizePastedHTML from './extensions/sanitizePastedHTML';
import ImageSettingsPanel from './components/ImageSettingsPanel';

// After the first sync, wait this long before deciding the shared doc is empty
// (lets existing content from the server/peers arrive first).
const COLLAB_SEED_SETTLE_MS = 600;
// ─── Main editor component ────────────────────────────────────────────────────
// collab* props are all OPTIONAL – omitting them keeps the editor in normal mode
const UnifiedTiptapEditor = ({ htmlContent, onChange, editorRef, isFullScreen, rfpId,
    // Collaboration props (all optional – editor degrades gracefully without them)
    collabYdoc,        // Y.Doc instance from useCollaboration
    collabProvider,    // WebsocketProvider instance from useCollaboration
    collabUser,        // { name, color, initials } – local user metadata
    collabStatus,      // 'connected' | 'connecting' | 'disconnected' | 'unavailable' | 'locked'
    collaborators,     // remote user array from useCollaboration
    collabReady,       // true once synced at least once (from useCollaboration)
    collabRetry,       // () => void – manual reconnect (from useCollaboration)
    onLocalEdit,       // () => void – called when THIS user changes the document (not peers)
    seedContent,       // html to start from if the shared draft is empty (draft beats main document)
    isReadOnly = false,
}) => {
    const wrapperRef = useRef(null);
    const editorElRef = useRef(null);
    const lastHtmlRef = useRef(htmlContent);
    const isLocalChangeRef = useRef(false);
    const hasSeededRef = useRef(false);
    // Section content as it was when this editor opened (the saved/REST version). The live
    // `htmlContent` prop drifts to the shared document once peers' edits stream in, so seeding
    // decisions must be made against this snapshot.
    const restHtmlRef = useRef(seedContent || htmlContent);
    const onLocalEditRef = useRef(onLocalEdit);

    const [imageContextMenu, setImageContextMenu] = React.useState(null);
    const [toast, setToast] = React.useState(null);
    const uploadAndInsertImageRef = useRef();
    const onChangeRef = useRef(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);
    useEffect(() => {
        onLocalEditRef.current = onLocalEdit;
    }, [onLocalEdit]);

    // ── Collaboration extensions ───────────────────────────────────────────────
    const hasCollab = Boolean(collabYdoc && collabProvider && collabStatus !== 'unavailable');

    const collabExtensions = hasCollab ? [
        Collaboration.configure({
            document: collabYdoc,
            field: 'default',
        }),
        CollaborationCaret.configure({
            provider: collabProvider,
            user: collabUser || { name: 'Collaborator', color: '#7C3AED' },
        }),
    ] : [];

    const isLocked = collabStatus === 'locked' || isReadOnly;
    // In collab mode the editor stays read-only until the first sync completes, so nobody
    // types into an empty, not-yet-seeded document. Temporary disconnects do NOT block
    // editing: `collabReady` stays true and local edits merge on reconnect.
    const canEdit = !isLocked && (!hasCollab || collabReady);

    // ── Typing indicator – notifies collab peers via awareness ────────────────
    const { notifyTyping } = useTypingIndicator(collabProvider || null);

    const editor = useEditor({
        editable: canEdit,
        extensions: [
            StarterKit.configure({
                bulletList: false,
                orderedList: false,
                history: !hasCollab,
            }),
            CustomBulletList,
            CustomOrderedList,
            ListItem,
            Table.configure({ resizable: true, lastColumnResizable: true, allowTableNodeSelection: true }),
            TableRow,
            CustomTableCell,
            CustomTableHeader,
            RaciDecorationExtension,
            Underline,
            CustomImage.configure({ HTMLAttributes: { class: 'editor-embedded-image' }, allowBase64: false }),
            TextAlign.configure({ types: ['heading', 'paragraph', 'tableCell', 'tableHeader'], alignments: ['left', 'center', 'right', 'justify'] }),
            Link.configure({ openOnClick: false, autolink: true, defaultProtocol: 'https', HTMLAttributes: { class: 'editor-link', target: '_blank', rel: 'noopener noreferrer' } }),
            TextStyle,
            Highlight.configure({ multicolor: true }),
            ...collabExtensions,
        ],
        content: hasCollab ? undefined : (htmlContent || ''),
        editorProps: {
            attributes: { class: 'seamless-tiptap-content' },
            transformPastedHTML(html) { return sanitizePastedHTML(html); },
            handleDOMEvents: {
                click: (view, event) => {
                    const target = event.target;
                    if (target.nodeName === 'IMG') {
                        const pos = view.posAtDOM(target);
                        if (pos >= 0) { const { state, dispatch } = view; dispatch(state.tr.setSelection(NodeSelection.create(state.doc, pos))); }
                        event.stopPropagation();
                        return true;
                    }
                    return false;
                },
                contextmenu: (view, event) => {
                    const target = event.target;
                    if (target.nodeName === 'IMG') {
                        event.preventDefault();
                        const pos = view.posAtDOM(target);
                        if (pos >= 0) { const { state, dispatch } = view; dispatch(state.tr.setSelection(NodeSelection.create(state.doc, pos))); }
                        setImageContextMenu({ x: event.clientX, y: event.clientY, target });
                        return true;
                    }
                    return false;
                },
                drop: (view, event) => {
                    const hasFiles = event.dataTransfer?.files?.length > 0;
                    if (hasFiles) {
                        const file = event.dataTransfer.files[0];
                        if (file.type.startsWith('image/')) {
                            event.preventDefault();
                            const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
                            if (coordinates && uploadAndInsertImageRef.current) uploadAndInsertImageRef.current(file, coordinates.pos);
                            else if (uploadAndInsertImageRef.current) uploadAndInsertImageRef.current(file);
                            return true;
                        }
                    }
                    return false;
                },
                paste: (view, event) => {
                    const hasItems = event.clipboardData?.items?.length > 0;
                    if (hasItems) {
                        const item = event.clipboardData.items[0];
                        if (item.type.startsWith('image/')) {
                            const file = item.getAsFile();
                            if (file && uploadAndInsertImageRef.current) { event.preventDefault(); uploadAndInsertImageRef.current(file); return true; }
                        }
                    }
                    return false;
                }
            }
        },
        onUpdate: ({ editor, transaction }) => {
            // Only genuine local edits count (remote Yjs updates also fire onUpdate).
            if (!(transaction && isChangeOrigin(transaction)) && onLocalEditRef.current) {
                onLocalEditRef.current();
            }
            const html = editor.getHTML();
            lastHtmlRef.current = html;
            isLocalChangeRef.current = true;
            clearTimeout(isLocalChangeRef._timer);
            isLocalChangeRef._timer = setTimeout(() => { isLocalChangeRef.current = false; }, 300);
            if (onChangeRef.current) onChangeRef.current(html);
            if (hasCollab && typeof notifyTyping === 'function') {
                notifyTyping();
            }
            applyRaciColors(editorElRef.current);
        },
    }, [hasCollab, collabYdoc, collabProvider, isLocked]);

    const showToast = (message, type = 'success') => { setToast({ message, type }); setTimeout(() => setToast(null), 4000); };

    // Ref-safe S3 image upload and cursor/drop insert
    uploadAndInsertImageRef.current = async (file, pos) => {
        if (!rfpId || !editor) return;
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) { showToast('Format must be JPG, JPEG, PNG, WEBP, GIF, or SVG.', 'error'); return; }
        if (file.size > 5 * 1024 * 1024) { showToast('Image size must be less than 5MB.', 'error'); return; }
        showToast('Uploading image to secure storage...', 'info');
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await uploadImage(rfpId, formData);
            const linkUrl = response.data?.url || response.data?.link;
            if (linkUrl) {
                const imageAttrs = { src: linkUrl, alt: 'Embedded Image', 'data-width-percent': '50', 'data-align': 'center' };
                if (pos !== undefined && pos !== null) editor.chain().focus().insertContentAt(pos, { type: 'image', attrs: imageAttrs }).run();
                else editor.chain().focus().setImage(imageAttrs).run();
                showToast('Image inserted successfully.');
            } else { throw new Error('No URL returned'); }
        } catch (err) { console.error('S3 drop/paste upload failed:', err); showToast('Failed to upload image.', 'error'); }
    };

    const handleDeleteImageForNode = async (img) => {
        const src = img.getAttribute('src');
        if (src && rfpId) {
            try {
                const filename = src.split('/').pop();
                await deleteImage(rfpId, filename);
                showToast('Image deleted successfully');
            } catch (err) { console.error('Failed to delete image:', err); showToast('Failed to delete image', 'error'); }
        }
        // Remove only this image node (never replace the whole document: with live editing that would
        // overwrite everyone's shared content).
        try {
            const pos = editor.view.posAtDOM(img, 0);
            editor.chain().focus().setNodeSelection(pos).deleteSelection().run();
        } catch (e) {
            console.warn('Could not remove image node:', e);
        }
        setImageContextMenu(null);
    };

    const handleDeleteImage = async () => {
        if (!imageContextMenu?.target) return;
        await handleDeleteImageForNode(imageContextMenu.target);
    };

    useEffect(() => {
        const handleClose = (e) => {
            if (e.target.closest('.image-settings-panel') || e.target.nodeName === 'IMG' || e.target.closest('.image-resize-handle') || e.target.closest('.simple-table-toolbar')) return;
            setImageContextMenu(null);
        };
        window.addEventListener('click', handleClose);
        return () => window.removeEventListener('click', handleClose);
    }, []);

    useEffect(() => {
        if (editor && editorRef) editorRef.current = editor;
        if (editor && editorElRef.current) applyRaciColors(editorElRef.current);
    }, [editor, editorRef]);

    const setEditorEl = useCallback((node) => {
        if (node) { editorElRef.current = node; setTimeout(() => applyRaciColors(node), 100); }
    }, []);

    useEffect(() => {
        if (!editor) return;
        const handler = () => applyRaciColors(editorElRef.current);
        editor.on('selectionUpdate', handler);
        editor.on('transaction', handler);
        return () => { editor.off('selectionUpdate', handler); editor.off('transaction', handler); };
    }, [editor]);

    // Reset seeding flag when switching documents or collaboration providers
    useEffect(() => {
        hasSeededRef.current = false;
    }, [collabYdoc, hasCollab]);

    // ── Sync external content changes (only in non-collab mode) ─────────────────
    useEffect(() => {
        if (!editor) return;
        // In collab mode, all sync is managed live by Yjs/WebSocket; do not overwrite
        if (hasCollab) return;
        if (htmlContent === lastHtmlRef.current) return;
        if (isLocalChangeRef.current) { lastHtmlRef.current = htmlContent; return; }
        if (htmlContent !== editor.getHTML()) {
            editor.commands.setContent(htmlContent || '', false);
            lastHtmlRef.current = htmlContent;
            setTimeout(() => applyRaciColors(editorElRef.current), 80);
        }
    }, [htmlContent, editor, hasCollab]);

    // ── Document Seeding: Collab & Standard Modes ────────────────────────────
    // Every section has a DRAFT (working copy, everyone's latest edits) and a MAIN document
    // (approved content only). The live room IS the draft: the server restores it, and every
    // editor shares it. So in collab mode:
    //   1. Wait for the first sync (never touch the doc before that).
    //   2. If the shared document already has content -> it is the draft: KEEP it. It is never
    //      replaced from the page's saved copy (for a lead that copy is the approved main
    //      document, which would wipe out the employees' draft).
    //   3. Only if the shared document is EMPTY, seed it once from the section content.
    // A short settle delay lets late state arrive first and stops two simultaneous
    // first-joiners from both seeding.
    // Standard (non-collab) mode simply loads the section content.
    useEffect(() => {
        if (!editor || hasSeededRef.current) return;

        if (hasCollab) {
            if (!collabReady) return;

            const fragment = collabYdoc?.getXmlFragment('default');
            const settleTimer = setTimeout(() => {
                if (hasSeededRef.current) return;
                hasSeededRef.current = true;

                const restHtml = restHtmlRef.current || htmlContent || '';
                const isDocEmpty = !fragment || fragment.length === 0;
                if (isDocEmpty) {
                    if (restHtml) {
                        console.log('[Editor Seed] Shared draft empty – seeding from section content.');
                        editor.commands.setContent(restHtml, false);
                        lastHtmlRef.current = restHtml;
                        if (onChangeRef.current) onChangeRef.current(restHtml);
                    }
                } else {
                    console.log('[Editor Seed] Shared draft has content – keeping it.');
                    const currentHtml = editor.getHTML();
                    lastHtmlRef.current = currentHtml;
                    if (onChangeRef.current) onChangeRef.current(currentHtml);
                }
            }, COLLAB_SEED_SETTLE_MS);
            return () => clearTimeout(settleTimer);
        } else {
            // Standard non-collab mode
            hasSeededRef.current = true;
            const initialHtml = restHtmlRef.current || htmlContent;
            if (initialHtml) {
                console.log('[Editor Seed] Standard mode – loading section content.');
                editor.commands.setContent(initialHtml, false);
                lastHtmlRef.current = initialHtml;
                if (onChangeRef.current) onChangeRef.current(initialHtml);
            }
        }
    }, [editor, hasCollab, collabReady, collabYdoc, htmlContent]);

    // Ensure editor editable property syncs dynamically with locked / sync status
    useEffect(() => {
        if (editor) {
            editor.setEditable(canEdit);
        }
    }, [editor, canEdit]);

    // ── Failsafe Seed Timer: Non-collab mode only (never overrides active collab sync) ──
    useEffect(() => {
        if (!editor || hasCollab) return;
        const failsafeTimer = setTimeout(() => {
            if (!hasSeededRef.current && (editor.isEmpty || editor.getText().trim() === '') && htmlContent) {
                console.warn('[Editor Seed] Standard mode failsafe. Loading initial draft content.');
                editor.commands.setContent(htmlContent, false);
                lastHtmlRef.current = htmlContent;
                hasSeededRef.current = true;
            }
        }, 1200);
        return () => clearTimeout(failsafeTimer);
    }, [editor, htmlContent, hasCollab]);

    if (!editor) return null;

    return (
        <div
            className={`unified-seamless-editor-wrapper${isFullScreen ? ' fullscreen-editor' : ''}`}
            ref={wrapperRef}
            onKeyDown={hasCollab ? notifyTyping : undefined}
        >
            {/* ── Collaboration bar (additive – only when collab props are provided) */}
            {(collabYdoc || collabStatus) && (
                <CollaborationBar
                    collaborators={collaborators || []}
                    status={collabStatus || 'unavailable'}
                    provider={collabProvider || null}
                    onRetry={collabRetry}
                />
            )}
            {collabStatus === 'locked' && (
                <div className="section-locked-banner" style={{
                    backgroundColor: '#FFF7ED',
                    border: '1px solid #FDBA74',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    margin: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    color: '#C2410C',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    boxShadow: '0 1px 3px rgba(194, 65, 12, 0.08)'
                }}>
                    <Lock size={16} style={{ flexShrink: 0 }} />
                    <span>This section was submitted and is now read-only</span>
                </div>
            )}
            {editor && (
                <BubbleMenu editor={editor} shouldShow={({ editor }) => editor.isActive('table')} tippyOptions={{ duration: 100, maxWidth: 'none', placement: 'top-start' }}>
                    <div className="simple-table-toolbar">
                        <button onClick={() => editor.chain().focus().addRowAfter().run()}>+ Row</button>
                        <button onClick={() => editor.chain().focus().addColumnAfter().run()}>+ Col</button>
                        <div className="v-divider" />
                        <button onClick={() => editor.chain().focus().mergeCells().run()}>Merge</button>
                        <button onClick={() => editor.chain().focus().splitCell().run()}>Split</button>
                        <div className="v-divider" />
                        <button onClick={() => editor.chain().focus().deleteRow().run()}>Del Row</button>
                        <button onClick={() => editor.chain().focus().deleteColumn().run()}>Del Col</button>
                        <div className="v-divider" />
                        <button onClick={() => editor.chain().focus().toggleHeaderRow().run()}>Header Row</button>
                        <div className="v-divider" />
                        <button onClick={() => editor.chain().focus().deleteTable().run()} className="table-delete-text">Delete Table</button>
                    </div>
                </BubbleMenu>
            )}
            {editor && (
                <BubbleMenu editor={editor} shouldShow={({ editor }) => editor.isActive('link')} tippyOptions={{ duration: 100, placement: 'bottom-start' }}>
                    <div className="simple-table-toolbar">
                        <button onClick={() => { const url = editor.getAttributes('link').href; if (url) window.open(url, '_blank'); }}>Open</button>
                        <div className="v-divider" />
                        <button onClick={() => editor.chain().focus().unsetLink().run()} className="table-delete-text">Remove Link</button>
                    </div>
                </BubbleMenu>
            )}
            <div className="unified-seamless-editor" ref={setEditorEl}>
                <EditorContent editor={editor} />
            </div>
            {imageContextMenu && (
                <ImageSettingsPanel
                    target={imageContextMenu.target}
                    editor={editor}
                    wrapperRef={wrapperRef}
                    rfpId={rfpId}
                    onClose={() => setImageContextMenu(null)}
                    onDelete={handleDeleteImage}
                />
            )}
            <SuccessToast toast={toast} />
        </div>
    );
};

export default UnifiedTiptapEditor;
